/**
 * 導航選單和側邊欄部落格連結語系自動處理器
 * 專門針對選單和側邊欄推薦區塊中的部落格連結進行語系前綴替換
 * 使用方法：將此代碼放在 theme.liquid 的 </body> 標籤前
 */

(function() {
  'use strict';

  /**
   * debounce 工具函式
   * @param {Function} func - 需要防抖的函式
   * @param {number} wait - 延遲時間(ms)
   * @returns {Function} 防抖後的函式
   */
  function debounce(func, wait) {
    let timeout;
    return function(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  // 配置設定
  const CONFIG = {
    // 選單連結選擇器（針對導航選單和側邊欄中的部落格連結）
    menuSelectors: [
      '.menu-lv-1__action[href*="/blogs/blog"]',  // 主選單連結
      '.menu-lv-2__action[href*="/blogs/blog"]',   // 子選單連結
      '.navPages-action[href*="/blogs/blog"]'     // 側邊欄推薦連結
    ],
    
    // 需要特殊處理的側邊欄連結（根據文字內容判斷）
    sidebarTextMap: {
      'en': {
        'Beginner Experience': '/blogs/blog?filter=Beginner%20Experience',
        'Master\'s Experience': '/blogs/blog?filter=Master%27s%20Experience', 
        'Experience Events': '/blogs/blog?filter=Experience%20Events'
      },
      'zh-tw': {
        '新手體驗': '/blogs/blog?filter=Beginner%20Experience',
        '大師體驗': '/blogs/blog?filter=Master%27s%20Experience',
        '體驗活動': '/blogs/blog?filter=Experience%20Events'
      },
      'ja': {
        'ビギナー体験': '/blogs/blog?filter=Beginner%20Experience',
        'マスター体験': '/blogs/blog?filter=Master%27s%20Experience',
        '体験イベント': '/blogs/blog?filter=Experience%20Events'
      }
    },
    
    // 支援的語系代碼
    supportedLangs: ['zh-tw', 'ja'],
    
    // 預設語系（英文）
    defaultLang: 'en'
  };

  /**
   * 偵測當前頁面的語系
   * @returns {string} 語系代碼 ('zh-tw', 'ja', 'en')
   */
  function detectCurrentLanguage() {
    const pathname = window.location.pathname;
    
    // 檢查URL是否包含語系前綴
    for (const lang of CONFIG.supportedLangs) {
      if (pathname.includes(`/${lang}/`)) {
        return lang;
      }
    }
    
    // 如果沒有找到語系前綴，返回預設語系
    return CONFIG.defaultLang;
  }

  /**
   * 替換部落格連結的語系前綴
   * @param {string} originalHref - 原始連結
   * @param {string} currentLang - 當前語系
   * @returns {string} 替換後的連結
   */
  function replaceBlogLinkPrefix(originalHref, currentLang) {
    if (!originalHref || !originalHref.includes('/blogs/blog')) {
      return originalHref;
    }

    // 解析URL組件
    let url;
    try {
      // 處理相對路徑
      if (originalHref.startsWith('/')) {
        url = new URL(originalHref, window.location.origin);
      } else {
        url = new URL(originalHref);
      }
    } catch (e) {
      // 如果URL解析失敗，嘗試簡單字串替換
      return simpleStringReplace(originalHref, currentLang);
    }

    let pathname = url.pathname;
    const search = url.search;
    const hash = url.hash;

    // 移除現有的語系前綴
    for (const lang of CONFIG.supportedLangs) {
      pathname = pathname.replace(`/${lang}/blogs/blog`, '/blogs/blog');
    }

    // 確保路徑正確
    if (!pathname.startsWith('/blogs/blog')) {
      pathname = '/blogs/blog';
    }

    // 添加新的語系前綴
    if (currentLang !== CONFIG.defaultLang) {
      pathname = `/${currentLang}${pathname}`;
    }

    return `${pathname}${search}${hash}`;
  }

  /**
   * 簡單字串替換（備用方法）
   * @param {string} href - 原始連結
   * @param {string} currentLang - 當前語系
   * @returns {string} 替換後的連結
   */
  function simpleStringReplace(href, currentLang) {
    let newHref = href;

    // 移除現有語系前綴
    for (const lang of CONFIG.supportedLangs) {
      newHref = newHref.replace(`/${lang}/blogs/blog`, '/blogs/blog');
    }

    // 添加新語系前綴
    if (currentLang !== CONFIG.defaultLang) {
      newHref = newHref.replace('/blogs/blog', `/${currentLang}/blogs/blog`);
    }

    return newHref;
  }

  /**
   * 處理側邊欄中的特殊連結（根據文字內容判斷）
   * @param {Element} link - 連結元素
   * @param {string} currentLang - 當前語系
   * @returns {boolean} 是否成功處理
   */
  function handleSidebarSpecialLink(link, currentLang) {
    const href = link.getAttribute('href');
    
    // 只處理 href="#" 或空的連結
    if (href && href !== '#' && href !== '') {
      return false;
    }
    
    // 獲取連結文字
    const textSpan = link.querySelector('.text');
    if (!textSpan) return false;
    
    const linkText = textSpan.textContent.trim();
    
    // 根據當前語系和文字內容找到對應的URL
    const langKey = currentLang === 'zh-tw' ? 'zh-tw' : currentLang === 'ja' ? 'ja' : 'en';
    const textMap = CONFIG.sidebarTextMap[langKey];
    
    if (textMap && textMap[linkText]) {
      let targetUrl = textMap[linkText];
      
      // 添加語系前綴
      if (currentLang !== CONFIG.defaultLang) {
        targetUrl = `/${currentLang}${targetUrl}`;
      }
      
      link.setAttribute('href', targetUrl);
      
      // 在開發環境中可以開啟此行來除錯
      // console.log(`Set sidebar link: "${linkText}" -> ${targetUrl}`);
      
      return true;
    }
    
    return false;
  }

  /**
   * 更新選單和側邊欄中的部落格連結
   */
  function updateMenuBlogLinks() {
    const currentLang = detectCurrentLanguage();
    
    // 處理正常的部落格連結
    const blogLinkSelectors = CONFIG.menuSelectors.map(selector => 
      selector.replace('[href*="/blogs/blog"]', '[href*="/blogs/blog"]')
    ).join(', ');
    
    const blogLinks = document.querySelectorAll(blogLinkSelectors);
    
    blogLinks.forEach(link => {
      const originalHref = link.getAttribute('href');
      
      if (originalHref && originalHref.includes('/blogs/blog')) {
        const newHref = replaceBlogLinkPrefix(originalHref, currentLang);
        
        // 只在連結有變化時才更新
        if (newHref !== originalHref) {
          link.setAttribute('href', newHref);
          
          // 在開發環境中可以開啟此行來除錯
          // console.log(`Updated menu blog link: ${originalHref} -> ${newHref}`);
        }
      }
    });
    
    // 處理側邊欄中的特殊連結（包括 href="#" 的情況）
    const sidebarLinks = document.querySelectorAll('.navPages-action');
    
    sidebarLinks.forEach(link => {
      const originalHref = link.getAttribute('href');
      
      // 先嘗試處理特殊連結（href="#" 或空的）
      if (!handleSidebarSpecialLink(link, currentLang)) {
        // 如果不是特殊連結，且包含部落格路徑，則按正常方式處理
        if (originalHref && originalHref.includes('/blogs/blog')) {
          const newHref = replaceBlogLinkPrefix(originalHref, currentLang);
          
          if (newHref !== originalHref) {
            link.setAttribute('href', newHref);
            
            // 在開發環境中可以開啟此行來除錯
            // console.log(`Updated sidebar blog link: ${originalHref} -> ${newHref}`);
          }
        }
      }
    });
  }

  // 使用 debounce 包裹 updateMenuBlogLinks，延遲 200ms
  const debouncedUpdateMenuBlogLinks = debounce(updateMenuBlogLinks, 200);

  /**
   * 監聽選單和側邊欄的動態變化
   */
  function observeMenuChanges() {
    // 僅監聽 .header__submenu 和 .sidebarBlock.sidebar-categories 兩個容器
    const menuContainers = Array.from(document.querySelectorAll('.header__submenu, .sidebarBlock.sidebar-categories'));
    
    if (menuContainers.length === 0) {
      // 找不到容器則不啟用 observer
      return;
    }

    menuContainers.forEach(container => {
      const observer = new MutationObserver(function(mutations) {
        let shouldUpdate = false;
        
        mutations.forEach(function(mutation) {
          if (mutation.type === 'childList') {
            // 檢查是否有新增的選單或側邊欄連結
            mutation.addedNodes.forEach(function(node) {
              if (node.nodeType === Node.ELEMENT_NODE) {
                const hasMenuLink = CONFIG.menuSelectors.some(selector => {
                  try {
                    return node.matches && node.matches(selector.replace('[href*="/blogs/blog"]', '')) ||
                           node.querySelector && node.querySelector(selector);
                  } catch (e) {
                    return false;
                  }
                });
                
                // 檢查是否有側邊欄連結
                const hasSidebarLink = node.matches && node.matches('.navPages-action') ||
                                     node.querySelector && node.querySelector('.navPages-action');
                
                if (hasMenuLink || hasSidebarLink) {
                  shouldUpdate = true;
                }
              }
            });
          }
          
          // 檢查href屬性變化（針對選單和側邊欄連結）
          if (mutation.type === 'attributes' && 
              mutation.attributeName === 'href' && 
              mutation.target.matches) {
                
            const isMenuLink = CONFIG.menuSelectors.some(selector => {
              try {
                return mutation.target.matches(selector);
              } catch (e) {
                return false;
              }
            });
            
            const isSidebarLink = mutation.target.matches('.navPages-action');
            
            if (isMenuLink || isSidebarLink) {
              shouldUpdate = true;
            }
          }
        });
        
        if (shouldUpdate) {
          // 使用 debounce 包裹，避免過於頻繁的更新
          debouncedUpdateMenuBlogLinks();
        }
      });
      
      // 開始監聽
      observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['href']
      });
    });
  }

  /**
   * 初始化函數
   */
  function init() {
    // 等待DOM載入完成
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        updateMenuBlogLinks();
        observeMenuChanges();
      });
    } else {
      // DOM已經載入完成
      updateMenuBlogLinks();
      observeMenuChanges();
    }
    
    // 監聽瀏覽器返回/前進按鈕
    window.addEventListener('popstate', function() {
      setTimeout(updateMenuBlogLinks, 100);
    });
    
    // 監聽語系切換（如果有相關事件）
    window.addEventListener('languageChanged', function() {
      setTimeout(updateMenuBlogLinks, 100);
    });
  }

  /**
   * 提供手動更新功能（可選）
   * 使用方法：在控制台執行 window.updateMenuBlogLinks()
   */
  window.updateMenuBlogLinks = updateMenuBlogLinks;

  // 啟動腳本
  init();

  // 在開發環境中可以開啟此行來確認腳本載入
  // console.log('選單和側邊欄部落格連結語系處理器已啟動，當前語系：', detectCurrentLanguage());

})();